import { useCallback, useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useAccountStore } from 'store/account-store'
import { useAssetStore } from 'store/asset-store'
import { useUserStore } from 'store/user-store'

export const getWorkspaceStatus = (
	accountStatus: string | undefined,
	subscriptionType: string | undefined
): string => {
	if (accountStatus === 'SETUP') {
		return 'SETUP'
	}
	if (subscriptionType === 'TRIAL') {
		return 'TRIAL'
	}
	return accountStatus || 'UNKNOWN'
}

const getSubscriptionId = (subscription: any): string | null => {
	if (!subscription) return null

	if (subscription.details?.id) {
		return subscription.details.id
	}

	if (subscription.details && typeof subscription.details === 'object') {
		if (subscription.details.subscription_id) {
			return subscription.details.subscription_id
		}
	}

	return subscription.id || null
}

export const usePostHogWorkspaceGroup = () => {
	const posthog = usePostHog()
	const account = useAccountStore((state) => state.account)
	const subscription = account?.billing?.subscription
	const plan = subscription?.plan?.tier ?? null
	const status = getWorkspaceStatus(account?.status, subscription?.type)

	useEffect(() => {
		if (account?.id)
			posthog?.group('workspace', account.id, {
				plan,
				status,
				allow_ineligible_ad_account:
					account.allow_ineligible_ad_account ?? false
			})
	}, [posthog, account?.id, plan, status, account?.allow_ineligible_ad_account])
}

export const useTrackPostHogEvent = () => {
	const posthog = usePostHog()
	const account = useAccountStore((state) => state.account)
	const subscription = useAccountStore((state) => state.subscription)
	const asset = useAssetStore((state) => state.asset)
	const user = useUserStore((state) => state.user)

	return useCallback(
		(
			eventName: string,
			eventPath: string | null,
			parameters?: Record<string, any> | null
		) => {
			if (!posthog) {
				return
			}

			const workspaceStatus = getWorkspaceStatus(
				account?.status,
				subscription?.type
			)

			const eventProperties: Record<string, any> = {
				account_id: account?.id || null,
				subscription_id: subscription ? getSubscriptionId(subscription) : null,
				asset_id: asset?.id || null,
				user_id: user?.id || null,
				event_date: new Date().toISOString(),
				event_path: eventPath,
				event_name: eventName,
				workspace_status: workspaceStatus
			}

			if (parameters) {
				eventProperties.parameters = parameters
			}

			posthog.capture(eventName, eventProperties)
		},
		[posthog, account, subscription, asset, user]
	)
}
